// @ts-strict
import { useState } from "react";
import { Link } from "react-router-dom";
import { VendorUtilities } from "../../utilities/VendorUtilities";

import Footer from "../shared/Footer";
import Alert from "../shared/Alert";
import { Button } from "../shared/Button";
import TextInput from "../shared/TextInput";
import ReplicatedInfo from "./ReplicatedInfo";

interface TeamRequirementResponse {
  user_exists: boolean;
  requires_team_id: boolean;
  requires_account_replacement?: boolean;
  message?: string;
}

const serverErrorMessage = "Unable to complete SAML login right now. Please try again later or contact support if the problem persists.";

const SAMLLogin = () => {
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [teamIdError, setTeamIdError] = useState(false);
  const [teamIdMessage, setTeamIdMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<'email' | 'team_id' | 'confirm_replacement'>('email');

  const clearErrors = () => {
    setEmailError(false);
    setEmailMessage("");
    setTeamIdError(false);
    setTeamIdMessage("");
    setError("");
  };

  const handleEmailChange = (value: string) => {
    if (emailError || error) {
      clearErrors();
    }
    setEmail(value);
  };

  const handleTeamIdChange = (value: string) => {
    if (teamIdError || error) {
      clearErrors();
    }
    setTeamId(value);
  };

  const validateEmail = () => {
    if (email.trim().length === 0) {
      setEmailError(true);
      setEmailMessage("Email address is required");
      return false;
    }
    if (!VendorUtilities.isEmailValid(email.trim())) {
      setEmailError(true);
      setEmailMessage("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const validateTeamId = () => {
    if (teamId.trim().length === 0) {
      setTeamIdError(true);
      setTeamIdMessage("Please enter your team ID");
      return false;
    }
    
    return true;
  };

  const checkTeamRequirement = async () => {
    setLoading(true);
    clearErrors();

    try {
      const checkUrl = `${import.meta.env.VITE_VENDOR_API_ENDPOINT}/v1/saml/check-team-requirement`;
      
      const response = await fetch(checkUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim()
        })
      });

      if (!response.ok) {
        let errorMessage = response.status >= 500
          ? serverErrorMessage
          : "Unable to authenticate with SAML using this email address. Please verify your email address or contact your administrator.";
        
        try {
          const errorData = await response.json();
          if (errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message;
          }
        } catch {
          // Keep the status-appropriate fallback for empty or non-JSON responses.
        }
        
        setError(errorMessage);
        setLoading(false);
        return;
      }

      const data: TeamRequirementResponse = await response.json();
      
      if (data.requires_team_id) {
        setStep('team_id');
        setLoading(false);
      } else {
        await continueWithSAML(data, '');
      }

    } catch (err) {
      console.error("Team requirement check failed:", err);
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError("Unable to connect to the authentication service. Please check your internet connection and try again.");
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to check team requirements. Please try again."
        );
      }
      setLoading(false);
    }
  };

  const continueWithSAML = async (data: TeamRequirementResponse, teamIdValue: string) => {
    setTeamId(teamIdValue);
    if (data.requires_account_replacement) {
      setStep('confirm_replacement');
      setLoading(false);
      return;
    }
    await initiateSAML(email.trim(), teamIdValue);
  };

  const initiateSAML = async (emailValue: string, teamIdValue: string) => {
    try {
      const initiateUrl = `${import.meta.env.VITE_VENDOR_API_ENDPOINT}/v1/saml/initiate`;
      
      // Build query parameters
      const params = new URLSearchParams({
        email: emailValue
      });
      
      if (teamIdValue) {
        params.append('team_id', teamIdValue);
      }
      
      // If validation succeeded, redirect to the initiate endpoint
      // Use GET with query parameters for the redirect (backend supports both GET and POST)
      window.location.href = `${initiateUrl}?${params.toString()}`;

    } catch (err) {
      console.error("SAML initiation failed:", err);
      setError("Failed to initiate SAML login. Please try again.");
      setLoading(false);
    }
  };

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail()) {
      return;
    }

    await checkTeamRequirement();
  };

  const onTeamIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTeamId()) {
      return;
    }

    setLoading(true);
    clearErrors();
    
    try {
      // First validate the team ID with the backend
      const checkUrl = `${import.meta.env.VITE_VENDOR_API_ENDPOINT}/v1/saml/check-team-requirement`;
      
      const response = await fetch(checkUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          team_id: teamId.trim()
        })
      });

      if (!response.ok) {
        let errorMessage = response.status >= 500
          ? serverErrorMessage
          : "Invalid team ID. Please contact your administrator for the correct team ID.";
        
        try {
          const errorData = await response.json();
          if (errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message;
          }
        } catch {
          // Use default message if JSON parsing fails
        }
        
        if (response.status >= 500) {
          setError(errorMessage);
        } else {
          setTeamIdError(true);
          setTeamIdMessage(errorMessage);
        }
        setLoading(false);
        return;
      }

      const data: TeamRequirementResponse = await response.json();
      await continueWithSAML(data, teamId.trim());

    } catch (err) {
      console.error("Team ID validation failed:", err);
      setTeamIdError(true);
      setTeamIdMessage("Failed to validate team ID. Please try again.");
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setTeamId('');
    clearErrors();
  };

  const onConfirmReplacement = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await initiateSAML(email.trim(), teamId.trim());
  };

  return (
      <div className="h-full w-full flex flex-col overflow-y-auto">
      <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
        <ReplicatedInfo />
        <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 auth-pages">
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            <p className="font-['Montserrat',sans-serif] font-bold text-indigo-500 text-[30px] !text-left max-lg:mt-[70px] leading-[1.4]">
              Sign in with SAML
            </p>
            {step === 'email' ? (
              <p className="text-sm text-gray-410 mt-1.5 mb-5 leading-[1.4]">
                Enter your work email address to continue with your organization's SAML identity provider.
              </p>
            ) : step === 'team_id' ? (
              <p className="text-sm text-gray-410 mt-1.5 mb-5 leading-[1.4]">
                Multiple teams found for @{email.split('@')[1]}. Please enter the team ID provided by your administrator.
              </p>
            ) : (
              <p className="text-sm text-gray-410 mt-1.5 mb-5 leading-[1.4]">
                Review what will happen after you sign in with your organization's identity provider.
              </p>
            )}
          </div>

          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            {step === 'email' ? (
              <form onSubmit={onEmailSubmit}>
                <div className="FormSection-wrapper">
                  {emailError ? (
                    <Alert variant="error" className="mb-1.5">
                      {emailMessage}
                    </Alert>
                  ) : null}
                  {error ? (
                    <Alert variant="error" className="mb-1.5">
                      {error}
                    </Alert>
                  ) : null}
                  <div className="mb-5">
                    <TextInput
                      type="email"
                      name="email"
                      placeholder="Enter your work email address"
                      autoFocus
                      value={email}
                      onChangeValue={handleEmailChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className="FormButton-wrapper flex">
                  <Button
                    kind="primary-purple"
                    size="large"
                    className="flex-none mt-2.5 mb-5 w-full !text-lg"
                    disabled={loading || email.trim().length === 0}
                    type="submit"
                  >
                    {loading ? "Checking team requirements..." : "Continue with SAML"}
                  </Button>
                </div>
              </form>
            ) : step === 'team_id' ? (
              <form onSubmit={onTeamIdSubmit}>
                <div className="FormSection-wrapper">
                  {teamIdError ? (
                    <Alert variant="error" className="mb-1.5">
                      {teamIdMessage}
                    </Alert>
                  ) : null}
                  {error ? (
                    <Alert variant="error" className="mb-1.5">
                      {error}
                    </Alert>
                  ) : null}
                  <div className="mb-5">
                    <TextInput
                      type="text"
                      name="teamId"
                      placeholder="Enter team ID"
                      autoFocus
                      value={teamId}
                      onChangeValue={handleTeamIdChange}
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-410 mt-1.5">
                      Your administrator will provide you with the correct team ID
                    </p>
                  </div>
                </div>
                <div className="FormButton-wrapper flex">
                  <Button
                    kind="primary-purple"
                    size="large"
                    className="flex-none mt-2.5 mb-5 w-full !text-lg"
                    disabled={loading || teamId.trim().length === 0}
                    type="submit"
                  >
                    {loading ? "Redirecting to your identity provider..." : "Continue with SAML"}
                  </Button>
                </div>
                <div className="mb-[15px]">
                  <p className="text-gray-410 text-center">
                    <button
                      type="button"
                      onClick={goBackToEmail}
                      disabled={loading}
                      className="bg-transparent border-none text-blue-300 font-semibold hover:underline cursor-pointer p-0"
                    >
                      Back to email entry
                    </button>
                  </p>
                </div>
              </form>
            ) : (
              <form onSubmit={onConfirmReplacement}>
                <Alert variant="warning" title="Signing in will replace your current account" className="mb-5">
                  <p>
                    If you successfully authenticate as <strong>{email.trim()}</strong>,
                    your current account will be replaced by an account on the SAML team.
                    You will no longer be able to sign in to your previous account with this email.
                  </p>
                  <p className="mt-2">
                    Your previous team's apps, licenses, and other resources will not be moved
                    to the SAML team. If you still need access to them, cancel and contact your
                    administrator before continuing.
                  </p>
                  <p className="mt-2">
                    Nothing changes until you successfully authenticate. Canceling here or
                    leaving the identity provider before completing sign-in keeps your current account.
                  </p>
                </Alert>
                {error && <Alert variant="error" className="mb-5">{error}</Alert>}
                <Button kind="primary-purple" size="large" className="w-full mb-3" type="submit" disabled={loading}>
                  {loading ? "Redirecting to your identity provider..." : "Continue to SAML and replace account"}
                </Button>
                <Button kind="secondary-gray" size="large" className="w-full mb-5" type="button" onClick={goBackToEmail} disabled={loading}>
                  Cancel and keep current account
                </Button>
              </form>
            )}
            <div className="mt-[15px] text-sm">
              <p className="text-gray-410 text-center">
                Don't have SAML set up?{" "}
                <Link
                  to="/login"
                  className="text-blue-300 font-semibold hover:underline cursor-pointer"
                >
                  Sign in with password
                </Link>
              </p>
            </div>
          </div>
          <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SAMLLogin;
